import React from 'react';
import {
  TaskOverlay, FormField, JalaliDatePicker, Alert, Button, Avatar, Spinner, SegmentedControl, Textarea,
} from '@parsina/framework';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { CT } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useReceptionStrings } from '../lib/receptionStrings';
import {
  rescheduleAppointment, setVisitMode, setAppointmentNote, fetchVideoAvailability,
  iranLocalToEpoch, epochToIranLocal, errorCode,
} from '../hooks/useDoctorBooking';
import { useDirtyClose } from './common/DirtyCloseGuard';
import { NOTE_MAX } from './ReceptionRowOpsDialogs';
import { userErrorMessage } from '../lib/userErrorMessage';

// ─────────────────────────────────────────────────────────────────────────────
// «ویرایش نوبت» (PM-0102, PM-0531) — the doctor edits one of their OWN
// appointments: whether the visit is online, its day and hour, and the note.
//
// The same three things the clinic desk's «ویرایش نوبت» edits, in the doctor's
// own پذیرش. The patient and the clinic are facts of the row and are shown, not
// asked. One TaskOverlay in its two shells (DESIGN_CONCEPT §5.2).
//
// Each field is its own write that already exists on the BFF — `setVisitMode`,
// `rescheduleAppointment`, `setAppointmentNote` — so a save sends ONLY what
// changed, time first: the shift and overlap rules can refuse it, and then
// nothing else is sent. If a later field is refused, the fields that did save
// stay saved (the form moves forward to them), the overlay stays open, and the
// refusal sits on the field it is about — a retry sends the rest only.
//
// A field the doctor may not change is shown LOCKED with the reason, never
// hidden: a missing control leaves them to look for where it went.
//
// The row is MOVED, never cancelled and booked again, so its invoice and any
// prepayment stay where they are; and turning a visit online or back changes its
// TYPE only — the price it was booked at stays (PM-0019).
// ─────────────────────────────────────────────────────────────────────────────

// Refusals that are about the instant the doctor picked.
const TIME_CODES = new Set(['SLOT_TAKEN', 'DOCTOR_NOT_PRESENT', 'BAD_USER_INPUT']);

export default function EditAppointmentDialog({ open, onClose, apt, onSaved, isMobile, timeLock = null }) {
  const i18n = useTranslation();
  const { formatDate, formatTime } = i18n;
  const s = useReceptionStrings();

  // A «ثبت سریع» row has only the desk's label and no patient file, and a visit's
  // type is set through the patient — so it has no type to edit.
  const hasPatient = Boolean(apt?.patientId);
  // «خارج از نوبت» (PM-0558): the row has no hour — its dateTime is the day's noon —
  // so the field starts empty and whatever the doctor picks is «تعیین ساعت».
  const untimed = apt?.schedulingMode === 'QUEUE';

  const fromRow = React.useMemo(() => ({
    when: apt?.dateTime && !untimed ? epochToIranLocal(apt.dateTime) : '',
    online: apt?.type === 'online',
    note: apt?.reminderNote || '',
  }), [apt?.dateTime, apt?.type, apt?.reminderNote, untimed]);

  // What is SAVED so far (starts as the row, moves forward as each field is
  // written) and what the doctor has typed.
  const [saved, setSaved] = React.useState(fromRow);
  const [form, setForm] = React.useState(fromRow);
  const [busy, setBusy] = React.useState(false);
  const [formError, setFormError] = React.useState('');
  const [whenError, setWhenError] = React.useState('');
  const [typeError, setTypeError] = React.useState('');
  const [noteError, setNoteError] = React.useState('');
  // Whether this deployment can hold a call at all — asked, not assumed.
  const [avail, setAvail] = React.useState({ loading: false, reason: null });

  // Every open starts from the row as it is now — never from the last attempt.
  React.useEffect(() => {
    if (!open) return;
    setSaved(fromRow);
    setForm(fromRow);
    setBusy(false);
    setFormError('');
    setWhenError('');
    setTypeError('');
    setNoteError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apt?.id]);

  React.useEffect(() => {
    if (!open || !hasPatient || !apt?.id) { setAvail({ loading: false, reason: null }); return undefined; }
    let alive = true;
    setAvail({ loading: true, reason: null });
    fetchVideoAvailability(apt.patientId, apt.id)
      .then((a) => { if (alive) setAvail({ loading: false, reason: a?.reason || null }); })
      // A read that failed is not «no calls here»: the control stays usable and
      // the BFF refuses again at write time if it must.
      .catch(() => { if (alive) setAvail({ loading: false, reason: null }); });
    return () => { alive = false; };
  }, [open, hasPatient, apt?.id, apt?.patientId]);

  const over = avail.reason === 'visit_over';
  // Going back to in-person is always allowed; turning ONLINE needs a media server.
  const noCalls = avail.reason === 'not_configured' && !saved.online;
  const typeNote = over ? s('dashboard.edit.typeLocked.over')
    : noCalls ? s('dashboard.edit.typeLocked.notConfigured')
      : s('dashboard.edit.typeHint');

  const timeChanged = !timeLock && Boolean(form.when) && form.when !== saved.when;
  const typeChanged = hasPatient && form.online !== saved.online;
  const noteChanged = form.note.trim() !== saved.note.trim();
  const changed = timeChanged || typeChanged || noteChanged;
  const { requestClose, guard } = useDirtyClose(changed && !busy, onClose);

  const fail = (err, { onField, readOnlyToForm = true } = {}) => {
    const message = userErrorMessage(err, i18n, 'errors.save.generic');
    // The clinic's subscription lapsed (PM-0219): said in the doctor's language.
    if (readOnlyToForm && errorCode(err) === 'SUBSCRIPTION_REQUIRED') setFormError(s('dashboard.book.readOnly'));
    else if (onField) onField(message, errorCode(err));
    else setFormError(message);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy || !apt?.id || !changed) return;
    setFormError(''); setWhenError(''); setTypeError(''); setNoteError('');

    let at = null;
    if (timeChanged) {
      at = iranLocalToEpoch(form.when);
      if (at == null) { setWhenError(s('dashboard.book.errors.when')); return; }
      // A patient already here may be given an hour that has passed.
      if (!untimed && at < Date.now() - 5 * 60 * 1000) { setWhenError(s('dashboard.book.errors.past')); return; }
    }

    setBusy(true);
    const done = {};
    let next = saved;
    let failed = false;
    try {
      if (timeChanged) {
        try {
          const moved = await rescheduleAppointment(apt.id, at);
          const landed = Number(moved?.dateTime) || at;
          done.time = {
            dayISO: form.when.slice(0, 10),
            when: s('dashboard.book.slotAt', { date: formatDate(landed, 'full'), time: formatTime(landed) }),
          };
          next = { ...next, when: form.when };
        } catch (err) {
          failed = true;
          fail(err, { onField: (m, code) => (TIME_CODES.has(code) ? setWhenError(m) : setFormError(m)) });
        }
      }
      if (!failed && typeChanged) {
        try {
          await setVisitMode(apt.patientId, apt.id, form.online);
          done.type = form.online ? 'online' : 'in_person';
          next = { ...next, online: form.online };
        } catch (err) {
          failed = true;
          fail(err, {
            onField: (m, code) => setTypeError(
              code === 'VIDEO_NOT_CONFIGURED' ? s('dashboard.edit.typeLocked.notConfigured')
                : code === 'VISIT_NOT_EDITABLE' ? s('dashboard.edit.typeLocked.over') : m),
          });
        }
      }
      if (!failed && noteChanged) {
        try {
          await setAppointmentNote(apt.id, form.note.trim());
          done.note = true;
          next = { ...next, note: form.note.trim() };
        } catch (err) {
          failed = true;
          fail(err, { onField: (m) => setNoteError(m) });
        }
      }
    } finally {
      setBusy(false);
    }
    setSaved(next);
    // The list behind the overlay changes for whatever DID save, failed or not.
    if (Object.keys(done).length) onSaved?.(done, apt);
    if (!failed) onClose?.();
  };

  const name = apt?.name || apt?.patient?.name || i18n.t('common.labels.patient');
  const at = Number(apt?.dateTime);

  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <Button variant="outline" type="button" onClick={requestClose} disabled={busy}>
        {s('dashboard.withdraw.keep')}
      </Button>
      <Button
        type="submit"
        form="edit-appointment-form"
        disabled={busy || !changed}
        aria-busy={busy || undefined}
        data-testid="edit-submit"
      >
        {busy && <Spinner size={16} color="currentColor" />}
        {s('dashboard.edit.submit')}
      </Button>
    </div>
  );

  const hintStyle = { fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.8 };

  return (
    <>
      <TaskOverlay
        open={open}
        onClose={requestClose}
        title={s('dashboard.edit.title')}
        footer={footer}
        busy={busy}
        size="sm"
        isMobile={isMobile}
        data-testid="edit-appointment"
      >
        <form
          id="edit-appointment-form"
          onSubmit={submit}
          noValidate
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {/* Who and when-now: facts of the row, shown so the doctor is sure which
              نوبت they are editing before they change it. */}
          <div
            data-testid="edit-current"
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              border: `1px solid ${C.border}`, borderRadius: 10, background: CT.surface2,
            }}
          >
            <Avatar name={name} size={36} bg={C.navy} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <bdi>{name}</bdi>
              </div>
              {untimed ? (
                <div style={{ fontSize: '0.75rem', color: C.textMuted, marginTop: 2 }}>
                  {s('dashboard.edit.untimedCurrent')}
                </div>
              ) : Number.isFinite(at) && at > 0 && (
                <div style={{ fontSize: '0.75rem', color: C.textMuted, marginTop: 2 }}>
                  {s('dashboard.reschedule.current', {
                    when: s('dashboard.book.slotAt', { date: formatDate(at, 'full'), time: formatTime(at) }),
                  })}
                </div>
              )}
            </div>
          </div>

          {formError && <Alert variant="error" data-testid="edit-error">{formError}</Alert>}

          {hasPatient && (
            <div data-testid="edit-type-field">
              <div
                id="edit-type-label"
                style={{ fontSize: '0.8125rem', fontWeight: 600, color: CT.textPrimary, marginBottom: '0.375rem' }}
              >
                {s('dashboard.edit.type')}
              </div>
              <SegmentedControl
                semantics="filter"
                ariaLabel={s('dashboard.edit.type')}
                aria-labelledby="edit-type-label"
                value={form.online ? 'online' : 'in_person'}
                onChange={(v) => { setForm((f) => ({ ...f, online: v === 'online' })); setTypeError(''); }}
                options={[
                  { value: 'in_person', label: s('dashboard.edit.inPerson'), disabled: busy || avail.loading || over },
                  { value: 'online', label: s('dashboard.edit.online'), disabled: busy || avail.loading || over || noCalls },
                ]}
                data-testid="edit-type"
              />
              {typeError
                ? <div role="alert" data-testid="edit-type-error" style={{ ...hintStyle, color: CT.dangerInk, marginTop: 6 }}>{typeError}</div>
                : <div data-testid="edit-type-hint" style={{ ...hintStyle, marginTop: 6 }}>{typeNote}</div>}
            </div>
          )}

          <div>
            <FormField label={s('dashboard.reschedule.newTime')} error={whenError} htmlFor="edit-when">
              <JalaliDatePicker
                id="edit-when"
                value={form.when}
                onChange={(v) => { setForm((f) => ({ ...f, when: v || '' })); setWhenError(''); }}
                withTime
                minuteStep={5}
                disabled={busy || Boolean(timeLock)}
                aria-invalid={whenError ? true : undefined}
                aria-label={s('dashboard.reschedule.newTime')}
                data-testid="edit-when"
              />
            </FormField>
            <div data-testid={timeLock ? 'edit-time-locked' : 'edit-time-hint'} style={{ ...hintStyle, marginTop: 6 }}>
              {timeLock ? s(`dashboard.edit.timeLocked.${timeLock}`)
                : s(untimed ? 'dashboard.edit.setTimeHint' : 'dashboard.reschedule.hint')}
            </div>
          </div>

          <div>
            <Textarea
              label={s('dashboard.edit.note')}
              value={form.note}
              onChange={(e) => { setForm((f) => ({ ...f, note: e.target.value.slice(0, NOTE_MAX) })); setNoteError(''); }}
              maxLength={NOTE_MAX}
              showCount
              rows={3}
              disabled={busy}
              error={noteError || undefined}
              data-testid="edit-note"
            />
          </div>
        </form>
      </TaskOverlay>
      {guard}
    </>
  );
}
